import {
  parseISO,
  startOfToday,
  differenceInMilliseconds,
  addMilliseconds,
  subDays,
} from "date-fns";

const BASELINE_FOR_DATES_AS_WRITTEN = parseISO("2024-04-30T16:25:00.00-07:00");
const BASELINE_GOING_FORWARD = subDays(startOfToday(), 17); // pretty much makes sure that nothing will be after today

const d = (strings: TemplateStringsArray, ...values: any[]) => {
  let composed = strings[0];

  for (let i = 1; i < strings.length; i++) {
    composed += `${values[i - 1]}${strings[i]}`;
  }

  try {
    const composedDate = parseISO(composed);

    const delta = differenceInMilliseconds(
      composedDate,
      BASELINE_FOR_DATES_AS_WRITTEN,
    );
    return addMilliseconds(BASELINE_GOING_FORWARD, delta).toISOString();
  } catch (e) {
    console.error(e);
    return composed;
  }
};

export type AlertType = string;

export type ISODateTime = string;
export type UUID = string;

export enum IssueStatus {
  Open = "Open",
  Assigned = "Assigned",
  Resolved = "Resolved",
  Muted = "Muted",
}

export interface TextHighlight {
  summary: string;
  pos?: {
    startIndex: number;
    endIndex: number;
  };
}

export interface AlertCitation {
  url: string;
  firstSeen: ISODateTime;
  context: string;
  highlights: TextHighlight[];
}

export interface Alert {
  type: string[];
  citations: AlertCitation[];
}

export interface User {
  id: UUID;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

export enum IssueActivityType {
  Commented = "Comment",
  Assigned = "Assign",
  Created = "Created",
  Resolved = "Resolved",
  Muted = "Muted",
}

export type ResolvedActivity = {
  id: UUID;
  date: ISODateTime;
  type: IssueActivityType.Resolved;
  resolvingUserId: string;
  resolutionType: IssueResolutionType;
  resolutionDescription: string;
};

export type CommentActivity = {
  id: UUID;
  date: ISODateTime;
  type: IssueActivityType.Commented;
  commentingUserId: string;
  commentBody: string;
};

export type AssignActivity = {
  id: UUID;
  date: ISODateTime;
  type: IssueActivityType.Assigned;
  assigningUserId: string;
  assignedUserId?: string | null | undefined;
};

export type CreatedActivity = {
  id: UUID;
  date: ISODateTime;
  type: IssueActivityType.Created;
};

export type MutedActivity = {
  id: UUID;
  date: ISODateTime;
  type: IssueActivityType.Muted;
  mutingUserId: string;
};

export type IssueActivity =
  | ResolvedActivity
  | CommentActivity
  | AssignActivity
  | CreatedActivity
  | MutedActivity;

export enum IssueResolutionType {
  Cleared = "Cleared",
  TerminatedMerchant = "Terminated Merchant",
  NoticeSent = "Notice Sent",
}

export interface IssueResolution {
  id: UUID;
  issueId: UUID;
  userId: UUID;
  type: IssueResolutionType;
  date: ISODateTime;
  details: string;
}

export interface MonitoringIssue {
  id: UUID;
  monitorId: UUID;
  date: ISODateTime;
  alerts: Alert[];
  assignee?: User;
  status: IssueStatus;
  activity: IssueActivity[];

  // denormalized fields
  domain: string; // monitor -> domain
}

export interface HomepageScan {
  id: UUID;
  date: ISODateTime;
  monitorId: UUID;
  issues: MonitoringIssue[];
}

export interface FullScan {
  id: UUID;
  date: ISODateTime;
  monitorId: UUID;
  issues: MonitoringIssue[];
}

export enum MonitorActivityType {
  Enrolled = "Enrolled",
  Unenrolled = "Unenrolled",
  Edited = "Edited",
  HomepageScan = "HomepageScan",
  FullScan = "FullScan",
}

type MonitorEnrolledActivity = {
  id: string;
  date: ISODateTime;
  type: MonitorActivityType.Enrolled;
  enrollingUserId: string;
};

type MonitorUnenrolledActivity = {
  id: string;
  date: ISODateTime;
  type: MonitorActivityType.Unenrolled;
  unenrollingUserId: string;
};

type MonitorHomepageScanActivity = {
  id: string;
  date: ISODateTime;
  type: MonitorActivityType.HomepageScan;
  detectedIssueIds: string[];
};

type MonitorFullScanActivity = {
  id: string;
  date: ISODateTime;
  type: MonitorActivityType.FullScan;
  detectedIssueIds: string[];
};

type MonitorEditedActivity = {
  id: string;
  date: ISODateTime;
  type: MonitorActivityType.Edited;
  editingUserId: string;
  editedFields: string[];
};

export type MonitorActivity =
  | MonitorEnrolledActivity
  | MonitorUnenrolledActivity
  | MonitorHomepageScanActivity
  | MonitorFullScanActivity
  | MonitorEditedActivity;

export const me: User = {
  id: "user.1",
  email: "danny@truebiz.io",
  firstName: "Danny",
  lastName: "Hakamian",
  fullName: "Danny Hakamian",
};

export const someoneElse: User = {
  id: "user.2",
  email: "drew@truebiz.io",
};

export const allPeople = [
  me,
  someoneElse,
  {
    id: "user.3",
    email: "max@truebiz.io",
  },
  {
    id: "user.4",
    firstName: "Jon",
    email: "jon@truebiz.io",
  },
  {
    id: "user.5",
    firstName: "Matthew",
    lastName: "Sewell",
    fullName: "Matthew Sewell",
    email: "matthew@truebiz.io",
  },
];

export const issues: MonitoringIssue[] = [
  {
    id: "issue.1",
    monitorId: "monitor.1",
    status: IssueStatus.Assigned,
    date: d`2024-05-17T17:53:00-07:00`,
    domain: "sixty3.com",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "https://rancholindo.com/wof.php?item=327153",
            firstSeen: d`2024-05-17T17:54:11-07:00`,
            context:
              "Calm on the range: these CBD lozenges will keep you relaxed and cool so you're sure to make the perfect shot every time.",
            highlights: [{ summary: "CBD lozenges" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.1.1",
        date: d`2024-05-17T19:27:23-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.1.2",
        date: d`2024-05-18T07:35:17-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: someoneElse.id,
      },
    ],
    assignee: someoneElse,
  },
  {
    id: "issue.2",
    monitorId: "monitor.2",
    status: IssueStatus.Assigned,
    date: d`2024-05-16T19:31:00-07:00`,
    domain: "spiderlingcafe.biz",
    alerts: [
      {
        type: ["Gambling"],
        citations: [
          {
            url: "https://spiderlingcafe.biz/backroomcafe/",
            firstSeen: d`2024-05-16T19:31:00-07:00`,
            context: "featuring 27 pie- and coffee-themed video poker machines",
            highlights: [{ summary: "video poker" }],
          },
        ],
      },
      {
        type: ["Gambling"],
        citations: [
          {
            url: "https://spiderlingcafe.biz/events/5552467",
            firstSeen: d`2024-05-16T19:31:00-07:00`,
            context:
              "blackjack tables run Tuesdays and Thursdays with our guest dealer Maryanne Applecorn",
            highlights: [{ summary: "blackjack" }, { summary: "dealer" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.2.1",
        date: d`2024-05-16T19:31:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.2.2",
        date: d`2024-05-17T08:55:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.3",
    monitorId: "monitor.3",
    status: IssueStatus.Resolved,
    date: d`2024-05-16T19:28:00-07:00`,
    domain: "fun-for-it.eu",
    alerts: [
      {
        type: ["Gambling"],
        citations: [
          {
            url: "https://fun-for-it.eu",
            firstSeen: d`2024-05-16T19:28:00-07:00`,
            context: "Sports betting has never been this addictive",
            highlights: [{ summary: "sports betting" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.3.1",
        date: d`2024-05-16T19:28:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.3.2",
        date: d`2024-05-19T13:05:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: someoneElse.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.3.3",
        date: d`2024-05-19T14:00:00-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.NoticeSent,
        resolutionDescription: "Email to Roz",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.4",
    monitorId: "monitor.4",
    status: IssueStatus.Assigned,
    date: d`2024-05-16T19:27:00-07:00`,
    domain: "rancholindo.com",
    alerts: [
      {
        type: ["Pharmaceutical"],
        citations: [
          {
            url: "https://www.rancholindo.com/our-vision/",
            firstSeen: d`2024-05-16T19:27:00-07:00`,
            context:
              "each psilocybin whisper orients our (well-)being within the post-Keynesian",
            highlights: [{ summary: "psilocybin" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.4.1",
        date: d`2024-05-16T19:27:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.4.2",
        date: d`2024-05-19T19:27:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.5",
    monitorId: "monitor.5",
    status: IssueStatus.Resolved,
    date: d`2024-05-16T19:27:00-07:00`,
    domain: "sawtooth.company.site",
    alerts: [
      {
        type: ["Cryptocurrency"],
        citations: [
          {
            url: "https://sawtooth.company.site/",
            firstSeen: d`2024-05-16T19:27:00-07:00`,
            context: "missing our SawBuckz initial coin offering (ICO)",
            highlights: [
              { summary: "initial coin offering" },
              { summary: "ICO" },
            ],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.5.1",
        date: d`2024-05-16T19:27:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.5.2",
        date: d`2024-05-16T22:27:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.5.3",
        date: d`2024-05-19T22:27:00-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.TerminatedMerchant,
        resolutionDescription: "Formal letter posted",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.6",
    monitorId: "monitor.6",
    status: IssueStatus.Resolved,
    date: d`2024-05-15T17:24:00-07:00`,
    domain: "every-jute-basket.com",
    alerts: [
      {
        type: ["Cannabis", "Subscription"],
        citations: [
          {
            url: "http://www.every-jute-basket.com/subscribe-now-and-save/",
            firstSeen: d`2024-05-15T17:24:00-07:00`,
            context:
              "for just $15.99/month a curated hemp basket of baskets will be",
            highlights: [{ summary: "$15.99/month" }, { summary: "hemp" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.6.1",
        date: d`2024-05-15T17:24:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.6.2",
        date: d`2024-05-20T17:24:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.6.3",
        date: d`2024-05-20T22:24:00-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.Cleared,
        resolutionDescription: "Subscription checked OK",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.7",
    monitorId: "monitor.7",
    status: IssueStatus.Assigned,
    date: d`2024-05-15T14:19:00-07:00`,
    domain: "handwringers.com",
    alerts: [
      {
        type: ["Adult"],
        citations: [
          {
            url: "https://www.handwringers.com/services/",
            firstSeen: d`2024-05-15T14:19:00-07:00`,
            context: "Exotic and erotic massages for the discerning customer",
            highlights: [{ summary: "erotic massages" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.7.1",
        date: d`2024-05-15T14:19:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.7.2",
        date: d`2024-05-17T08:22:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.8",
    monitorId: "monitor.8",
    status: IssueStatus.Assigned,
    date: d`2024-05-15T11:32:00-07:00`,
    domain: "wenderling.com",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "http://wenderling.com/237155.php",
            firstSeen: d`2024-05-15T11:32:00-07:00`,
            context:
              "a crisp and refreshing lager infused with CBD, minimally-hopped with a low ABV",
            highlights: [{ summary: "cbd" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.8.1",
        date: d`2024-05-15T11:32:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.8.2",
        date: d`2024-05-15T11:41:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.9",
    monitorId: "monitor.9",
    status: IssueStatus.Assigned,
    date: d`2024-05-15T05:51:00-07:00`,
    domain: "balinda.com",
    alerts: [
      {
        type: ["Cyberlocker", "Adult"],
        citations: [
          {
            url: "https://www.balinda.com/news/",
            firstSeen: d`2024-05-15T05:51:00-07:00`,
            context:
              "Upload any content - including deepfake porn to us today!",
            highlights: [
              { summary: "Upload any content" },
              { summary: "deepfake porn" },
            ],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.9.1",
        date: d`2024-05-15T05:51:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.9.2",
        date: d`2024-05-15T12:02:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.9.3",
        date: d`2024-05-15T12:37:00-07:00`,
        type: IssueActivityType.Commented,
        commentBody: "Cross-referencing with other files",
        commentingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.10",
    monitorId: "monitor.10",
    status: IssueStatus.Assigned,
    date: d`2024-05-10T23:53:00-07:00`,
    domain: "gracklebros.company.site",
    alerts: [
      {
        type: ["Tobacco"],
        citations: [
          {
            url: "https://gracklebros.company.site/",
            firstSeen: d`2024-05-10T23:53:00-07:00`,
            context: "RARE Hungarian-made cigars and fine tobacco",
            highlights: [{ summary: "cigars" }, { summary: "tobacco" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.10.1",
        date: d`2024-05-10T23:53:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.10.2",
        date: d`2024-05-10T23:59:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.11",
    monitorId: "monitor.11",
    status: IssueStatus.Resolved,
    date: d`2024-05-10T22:15:00-07:00`,
    domain: "kabuki.net",
    alerts: [
      {
        type: ["Securities"],
        citations: [
          {
            url: "https://www.kabuki.net/about/",
            firstSeen: d`2024-05-10T22:15:00-07:00`,
            context: "discount binary options trading",
            highlights: [{ summary: "binary options trading" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.11.1",
        date: d`2024-05-10T22:15:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.11.2",
        date: d`2024-05-10T22:15:01-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.11.3",
        date: d`2024-05-10T22:33:01-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.TerminatedMerchant,
        resolutionDescription: "Formal letter posted",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.12",
    monitorId: "monitor.12",
    status: IssueStatus.Resolved,
    date: d`2024-05-10T13:12:00-07:00`,
    domain: "islandfountain.com",
    alerts: [
      {
        type: ["Adult"],
        citations: [
          {
            url: "https://www.islandfountain.com/",
            firstSeen: d`2024-05-10T13:12:00-07:00`,
            context: "the services provides by our escorts are world class",
            highlights: [{ summary: "escorts" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.12.1",
        date: d`2024-05-10T13:12:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.12.2",
        date: d`2024-05-10T13:18:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.12.3",
        date: d`2024-05-11T13:18:00-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.NoticeSent,
        resolutionDescription: "Email and formal letter",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.13",
    monitorId: "monitor.13",
    status: IssueStatus.Resolved,
    date: d`2024-05-10T04:41:00-07:00`,
    domain: "slothtower.eu",
    alerts: [
      {
        type: ["Gambling"],
        citations: [
          {
            url: "http://slothtower.eu/gamez",
            firstSeen: d`2024-05-10T04:41:00-07:00`,
            context: "a high-stakes bet against all odds in a game of chance",
            highlights: [
              { summary: "bet" },
              { summary: "odds" },
              { summary: "game of chance" },
            ],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.13.1",
        date: d`2024-05-10T04:41:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.13.2",
        date: d`2024-05-10T06:37:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: someoneElse.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.13.3",
        date: d`2024-05-10T09:22:00-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.Cleared,
        resolutionDescription: "Videogames OK",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.14",
    monitorId: "monitor.14",
    status: IssueStatus.Open,
    date: d`2024-05-02T21:25:00-07:00`,
    domain: "puffdreem.com",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "http://puffdreem.com/",
            firstSeen: d`2024-05-02T21:25:00-07:00`,
            context: "Buy your CBD Carts and other vapes online and enjoy fast",
            highlights: [{ summary: "CBD Carts" }, { summary: "vapes" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.14.1",
        date: d`2024-05-02T21:25:00-07:00`,
        type: IssueActivityType.Created,
      },
    ],
    assignee: undefined,
  },
  {
    id: "issue.15",
    monitorId: "monitor.15",
    status: IssueStatus.Resolved,
    date: d`2024-05-02T19:18:00-07:00`,
    domain: "healthstars.com",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "https://www.healthstars.com/",
            firstSeen: d`2024-05-02T19:18:00-07:00`,
            context:
              "great deal of attention is paid to the amount of THC and CBD in",
            highlights: [{ summary: "THC" }, { summary: "CBD" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.15.1",
        date: d`2024-05-02T19:18:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.15.2",
        date: d`2024-05-02T19:20:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: someoneElse.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.15.3",
        date: d`2024-05-02T19:53:00-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.NoticeSent,
        resolutionDescription: "Email and formal letter",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.16",
    monitorId: "monitor.16",
    status: IssueStatus.Assigned,
    date: d`2024-05-01T08:16:00-07:00`,
    domain: "reconquista-bar.biz",
    alerts: [
      {
        type: ["Pharmaceutical"],
        citations: [
          {
            url: "https://www.reconquista-bar.biz/",
            firstSeen: d`2024-05-01T08:16:00-07:00`,
            context:
              "our cocktail program offers a wide variety of microdosed mushroom infused drinks sure to enthrall",
            highlights: [{ summary: "microdosed mushroom" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.16.1",
        date: d`2024-05-01T08:16:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.16.2",
        date: d`2024-05-01T08:55:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.17",
    monitorId: "monitor.17",
    status: IssueStatus.Resolved,
    date: d`2024-04-30T15:02:00-07:00`,
    domain: "marietta-foods.com",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "https://marietta-foods.com/cannabliss",
            firstSeen: d`2024-04-30T15:02:00-07:00`,
            context: "fibers and locally-sourced Cannabis sativa delight",
            highlights: [{ summary: "Cannabis sativa" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.17.1",
        date: d`2024-04-30T15:02:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.17.2",
        date: d`2024-04-30T15:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
      {
        id: "issue.activity.17.3",
        date: d`2024-05-03T11:11:00-07:00`,
        type: IssueActivityType.Resolved,
        resolutionType: IssueResolutionType.Cleared,
        resolutionDescription: "Non-CBD based hemp products OK",
        resolvingUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.18",
    monitorId: "monitor.18",
    status: IssueStatus.Assigned,
    date: d`2024-04-30T16:02:00-07:00`,
    domain: "everyday-everything.com",
    alerts: [
      {
        type: ["Subscription"],
        citations: [
          {
            url: "https://everyday-everything.com/products/124",
            firstSeen: d`2024-04-30T15:02:00-07:00`,
            context: "$14.99/month for regular deliveries of apples",
            highlights: [{ summary: "$14.99/month" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.18.1",
        date: d`2024-04-30T16:02:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.18.2",
        date: d`2024-04-30T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.19",
    monitorId: "monitor.19",
    status: IssueStatus.Assigned,
    date: d`2024-05-02T09:14:00-07:00`,
    domain: "fourex.com",
    alerts: [
      {
        type: ["Securities"],
        citations: [
          {
            url: "https://fourex.com/",
            firstSeen: d`2024-04-30T15:02:00-07:00`,
            context: "Forex trading with the #1 broker",
            highlights: [{ summary: "forex trading" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.19.1",
        date: d`2024-05-02T09:14:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.19.2",
        date: d`2024-05-02T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.20",
    monitorId: "monitor.20",
    status: IssueStatus.Assigned,
    date: d`2024-05-02T09:14:00-07:00`,
    domain: "moviesuploadanywhere.com",
    alerts: [
      {
        type: ["Cyberlocker", "Subscription"],
        citations: [
          {
            url: "https://moviesuploadanywhere.com/",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context: "Upload and watch movies anywhere for $4.95/month",
            highlights: [
              { summary: "Upload" },
              { summary: "movies" },
              { summary: "$4.95/month" },
            ],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.20.1",
        date: d`2024-05-02T09:14:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.20.2",
        date: d`2024-05-02T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.21",
    monitorId: "monitor.21",
    status: IssueStatus.Assigned,
    date: d`2024-05-02T16:40:00-07:00`,
    domain: "racing4fun.com",
    alerts: [
      {
        type: ["Gambling"],
        citations: [
          {
            url: "https://racing4fun.com/location/4228167933/events/123866",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context:
              "Come on down Thursday night and take a bet on your favorite horse at the track!",
            highlights: [{ summary: "take a bet" }],
          },
        ],
      },
      {
        type: ["Adult", "Gambling"],
        citations: [
          {
            url: "https://racing4fun.com/location/4228167933/events/123866",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context:
              "Visit Friday to see your favorite risque watersports race and bet on who wins!",
            highlights: [{ summary: "bet" }, { summary: "risque watersports" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.21.1",
        date: d`2024-05-02T09:14:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.21.2",
        date: d`2024-05-02T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.22",
    monitorId: "monitor.22",
    status: IssueStatus.Assigned,
    date: d`2024-05-02T16:40:00-07:00`,
    domain: "forthepeopleclub.com",
    alerts: [
      {
        type: ["Cannabis", "Subscription"],
        citations: [
          {
            url: "https://forthepeopleclub.com/items/weekly_gummies",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context:
              "Sign up today to get 200mg of delta-9 gummies delivered weekly",
            highlights: [
              { summary: "Sign up today" },
              { summary: "delta-9 gummies" },
              { summary: "weekly" },
            ],
          },
        ],
      },
      {
        type: ["Cryptocurrency"],
        citations: [
          {
            url: "https://forthepeopleclub.com/terms",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context: "You can pay us in USD via credit card or using bitcoin!",
            highlights: [{ summary: "bitcoin" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.22.1",
        date: d`2024-05-02T09:14:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.22.2",
        date: d`2024-05-02T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.23",
    monitorId: "monitor.23",
    status: IssueStatus.Assigned,
    date: d`2024-05-02T16:40:00-07:00`,
    domain: "carringtonoutpost.com",
    alerts: [
      {
        type: ["Tobacco"],
        citations: [
          {
            url: "https://carringtonoutpost.com/items/882461",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context: "This package comes with 4 philly blunts inside...",
            highlights: [{ summary: "philly blunts" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.23.1",
        date: d`2024-05-02T09:14:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.23.2",
        date: d`2024-05-02T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.24",
    monitorId: "monitor.24",
    status: IssueStatus.Assigned,
    date: d`2024-05-02T16:40:00-07:00`,
    domain: "thegreenshoetheater.com",
    alerts: [
      {
        type: ["Adult"],
        citations: [
          {
            url: "https://thegreenshoetheater.com/events/26613587742",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context: "featuring exotic dances performing fully nude",
            highlights: [
              { summary: "exotic dances" },
              { summary: "fully nude" },
            ],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.24.1",
        date: d`2024-05-02T09:14:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.24.2",
        date: d`2024-05-02T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.25",
    monitorId: "monitor.25",
    status: IssueStatus.Assigned,
    date: d`2024-05-02T16:40:00-07:00`,
    domain: "phoenixfantasygames.com",
    alerts: [
      {
        type: ["Gambling"],
        citations: [
          {
            url: "https://phoenixfantasygames.com/",
            firstSeen: d`2024-05-02T14:02:00-07:00`,
            context:
              "Build your fantasy sports team and be eligible to win our ultimate prize!",
            highlights: [
              { summary: "fantasy sports team" },
              { summary: "ultimate prize" },
            ],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.25.1",
        date: d`2024-05-02T09:14:00-07:00`,
        type: IssueActivityType.Created,
      },
      {
        id: "issue.activity.25.2",
        date: d`2024-05-02T16:40:00-07:00`,
        type: IssueActivityType.Assigned,
        assigningUserId: me.id,
        assignedUserId: me.id,
      },
    ],
    assignee: me,
  },
  {
    id: "issue.26",
    monitorId: "monitor.26",
    status: IssueStatus.Open,
    date: d`2024-05-02T21:25:00-07:00`,
    domain: "passthepuff.co.uk",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "http://passthepuff.co.uk/",
            firstSeen: d`2024-05-02T21:25:00-07:00`,
            context: "Buy your CBD Carts and other vapes online and enjoy fast",
            highlights: [{ summary: "CBD Carts" }, { summary: "vapes" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.26.1",
        date: d`2024-05-02T21:25:00-07:00`,
        type: IssueActivityType.Created,
      },
    ],
    assignee: undefined,
  },
  {
    id: "issue.27",
    monitorId: "monitor.27",
    status: IssueStatus.Open,
    date: d`2024-05-02T21:25:00-07:00`,
    domain: "smokemouse.net",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "http://smokemouse.net/",
            firstSeen: d`2024-05-02T21:25:00-07:00`,
            context: "Buy your CBD Carts and other vapes online and enjoy fast",
            highlights: [{ summary: "CBD Carts" }, { summary: "vapes" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.27.1",
        date: d`2024-05-02T21:25:00-07:00`,
        type: IssueActivityType.Created,
      },
    ],
    assignee: undefined,
  },
  {
    id: "issue.28",
    monitorId: "monitor.28",
    status: IssueStatus.Open,
    date: d`2024-05-02T21:25:00-07:00`,
    domain: "becausewegothigh.net",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "http://becausewegothigh.net/",
            firstSeen: d`2024-05-02T21:25:00-07:00`,
            context: "Buy your CBD Carts and other vapes online and enjoy fast",
            highlights: [{ summary: "CBD Carts" }, { summary: "vapes" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.28.1",
        date: d`2024-05-02T21:25:00-07:00`,
        type: IssueActivityType.Created,
      },
    ],
    assignee: undefined,
  },
  {
    id: "issue.29",
    monitorId: "monitor.29",
    status: IssueStatus.Open,
    date: d`2024-05-02T21:25:00-07:00`,
    domain: "crazythc.net",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "http://crazythc.net/",
            firstSeen: d`2024-05-02T21:25:00-07:00`,
            context: "Buy your CBD Carts and other vapes online and enjoy fast",
            highlights: [{ summary: "CBD Carts" }, { summary: "vapes" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.27.1",
        date: d`2024-05-02T21:25:00-07:00`,
        type: IssueActivityType.Created,
      },
    ],
    assignee: undefined,
  },
  {
    id: "issue.30",
    monitorId: "monitor.30",
    status: IssueStatus.Open,
    date: d`2024-05-02T21:25:00-07:00`,
    domain: "weekseed.net",
    alerts: [
      {
        type: ["Cannabis"],
        citations: [
          {
            url: "http://weekseed.net/",
            firstSeen: d`2024-05-02T21:25:00-07:00`,
            context: "Buy your CBD Carts and other vapes online and enjoy fast",
            highlights: [{ summary: "CBD Carts" }, { summary: "vapes" }],
          },
        ],
      },
    ],
    activity: [
      {
        id: "issue.activity.30.1",
        date: d`2024-05-02T21:25:00-07:00`,
        type: IssueActivityType.Created,
      },
    ],
    assignee: undefined,
  },
];

export interface Monitor {
  // meta
  id: string;
  lastRun?: ISODateTime;
  nextRun?: ISODateTime;
  _deleted?: boolean;
  activity: MonitorActivity[];

  // required
  domain: string;
  merchantMcc: string;

  // optional
  merchantName?: string;
  merchantDescription?: string;
  externalReferenceId?: string;
  emailAddress?: string;
  phoneNumber?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    stateProvince?: string;
    postalCode: string;
    country: string;
  };
}

export const monitors: Monitor[] = [
  {
    id: "monitor.1",
    domain: "sixty3.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-12T13:27:00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.1.activity.19",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-18T00:00:00.00-06:00`,
        detectedIssueIds: ["issue.1"],
      },
      {
        id: "monitor.1.activity.18",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-17T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.17",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-16T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.16",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-15T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.15",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-14T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.14",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-13T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.13",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-12T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.12",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-11T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.11",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-10T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.10",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-09T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.9",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-08T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.8",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-07T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.7",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-06T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.6",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-05T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.5",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-04T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.4",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-03T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.3",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-02T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.2",
        type: MonitorActivityType.FullScan,
        date: d`2024-05-01T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.1.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.2",
    domain: "spiderlingcafe.biz",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.2.activity.13",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-12T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.12",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-11T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.11",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-10T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.10",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-09T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.9",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-08T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.8",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-07T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.7",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-06T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.6",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-05T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.5",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-04T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.4",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-03T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.3",
        type: MonitorActivityType.HomepageScan,
        date: d`2024-05-02T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.2",
        type: MonitorActivityType.FullScan,
        date: d`2024-05-01T00:00:00.00-06:00`,
        detectedIssueIds: [],
      },
      {
        id: "monitor.2.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.3",
    domain: "fun-for-it.eu",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.3.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.4",
    domain: "rancholindo.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-29T00:00:00.00-07:00`,
    nextRun: undefined,
    activity: [
      {
        id: "monitor.4.activity.2",
        type: MonitorActivityType.Unenrolled,
        date: d`2024-05-30T00:00:00.00-07:00`,
        unenrollingUserId: someoneElse.id,
      },
      {
        id: "monitor.4.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
    _deleted: true,
  },
  {
    id: "monitor.5",
    domain: "sawtooth.company.site",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.5.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.6",
    domain: "every-jute-basket.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.6.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.7",
    domain: "handwringers.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.7.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.8",
    domain: "wenderling.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.8.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.9",
    domain: "balinda.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-06-01T00:00:00Z`,
    activity: [
      {
        id: "monitor.9.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.10",
    domain: "gracklebros.company.site",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.10.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.11",
    domain: "kabuki.net",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.11.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.12",
    domain: "islandfountain.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.12.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.13",
    domain: "slothtower.eu",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.13.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.14",
    domain: "puffdreem.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.14.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.15",
    domain: "healthstars.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.15.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.16",
    domain: "reconquista-bar.biz",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.16.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.17",
    domain: "marietta-foods.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.17.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T06:30:57.00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.18",
    domain: "everyday-everything.com",
    merchantMcc: "5411",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.18.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-04-30T16:02:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.19",
    domain: "fourex.com",
    merchantMcc: "5948",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.19.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.20",
    domain: "moviesuploadanywhere.com",
    merchantMcc: "4816",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.20.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.21",
    domain: "racing4fun.com",
    merchantMcc: "7995",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.21.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.22",
    domain: "forthepeopleclub.com",
    merchantMcc: "5262",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.22.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.23",
    domain: "carringtonoutpost.com",
    merchantMcc: "5262",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.23.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.24",
    domain: "thegreenshoetheater.com",
    merchantMcc: "7922",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.24.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.25",
    domain: "phoenixfantasygames.com",
    merchantMcc: "5816",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.25.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.26",
    domain: "passthepuff.co.uk",
    merchantMcc: "5912",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.26.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.27",
    domain: "smokemouse.net",
    merchantMcc: "5912",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.27.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.28",
    domain: "becausewegothigh.net",
    merchantMcc: "5912",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.28.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.29",
    domain: "crazythc.net",
    merchantMcc: "5912",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.29.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
  {
    id: "monitor.30",
    domain: "weekseed.net",
    merchantMcc: "5912",
    lastRun: d`2024-05-18T00:22:00.00-07:00`,
    nextRun: d`2024-05-19T00:00:00.00-07:00`,
    activity: [
      {
        id: "monitor.30.activity.1",
        type: MonitorActivityType.Enrolled,
        date: d`2024-05-02T09:14:00-07:00`,
        enrollingUserId: me.id,
      },
    ],
  },
];
